// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState, getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    getCartState,
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { xGoogleAnalyticsState } from "@pacific/App/Store/Reducers";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { productAddToCartButtonStyles } from "@pacific/Overrides/Components/ProductAddToCartButton";
import { mergeWithCss } from "@pacific/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";

interface OwnProps {
    styles?: ButtonPresentationProps;
}

// <XNG-Changes>
const buttonStyles: ButtonPresentationProps = {
    ...productAddToCartButtonStyles,
};
// </XNG-Changes>

const mapStateToProps = (state: xGoogleAnalyticsState) => {
    const { cartId, isPlacingOrder, isCheckingOutWithPayPay } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const billToState = getBillToState(state, cartState.value?.billToId);
    const shipToState = getShipToState(state, cartState.value?.shipToId);

    const { sessionId, redirectResult } = parseQueryString<{ sessionId?: string; redirectResult?: string }>(
        getLocation(state).search,
    );

    return {
        isDisabled:
            cartState.isLoading ||
            billToState.isLoading ||
            !billToState.value ||
            shipToState.isLoading ||
            !shipToState.value ||
            !billToState.value.address1 ||
            !shipToState.value.address1 ||
            isPlacingOrder ||
            isCheckingOutWithPayPay ||
            (!!sessionId && !!redirectResult) ||
            hasProductsWithInvalidPrice(cartState.value) ||
            cartState.value?.hasInsufficientInventory === true,
        cart: cartState.value,
        xGoogleAnalytics: state.xGoogleAnalytics,
        session: getSession(state),
        discountTotal: getDiscountTotal(getCurrentPromotionsDataView(state).value || []),
        currentBillTo: getCurrentBillToState(state).value,
        isAuthenticated: xIsAuthenticated(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const CheckoutReviewAndSubmitPlaceOrderButton: FC<Props> = ({
    isDisabled,
    styles: extendedStyles,
    // XNG-Change
    cart,
    session,
    currentBillTo,
    discountTotal,
    xGoogleAnalytics,
    isAuthenticated,
}) => {
    // <XNG-Changes>
    const [styles] = useState(() => mergeWithCss(buttonStyles, extendedStyles));
    // GA4 Checkout Funnel
    useEffect(() => {
        if (xGoogleAnalytics.checkoutFlowEvents) {
            (window as any)._GaCheckoutFunnel(3, cart, session, isAuthenticated, currentBillTo, discountTotal);
        }
    }, [cart?.paymentMethod]);
    // </XNG-Changes>
    const handleClick = () => {
        document.getElementById("reviewAndSubmitPaymentForm-submit")?.click();
    };
    return (
        <Button
            type="submit"
            disabled={isDisabled}
            data-test-selector="checkoutReviewAndSubmit_placeOrder"
            {...styles}
            onClick={handleClick}
            aria-label="Place Order"
        >
            {translate("Place Order")}
        </Button>
    );
};

export default connect(mapStateToProps)(CheckoutReviewAndSubmitPlaceOrderButton);
